/**
 * Sprint 59 — Application Checklist + Document Tracker
 *
 * SubsidyChecklist: step-by-step guide for subsidy applications.
 * Auto-checks are read-only; manual steps can be toggled.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

// ── Step status icons ────────────────────────────────────────────────────────

function StatusIcon({ status, size = 'md' }) {
  const sz = size === 'sm' ? 'w-5 h-5 text-xs' : 'w-7 h-7 text-sm';
  if (status === 'done') {
    return (
      <div className={`${sz} rounded-full bg-green-500/20 border border-green-500 flex items-center justify-center text-green-400 shrink-0`}>
        ✓
      </div>
    );
  }
  if (status === 'partial') {
    return (
      <div className={`${sz} rounded-full bg-yellow-500/20 border border-yellow-500 flex items-center justify-center text-yellow-400 shrink-0`}>
        ◑
      </div>
    );
  }
  return (
    <div className={`${sz} rounded-full bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] flex items-center justify-center text-[var(--aif-text-muted)] shrink-0`}>
      ○
    </div>
  );
}

// ── CompletionBar ────────────────────────────────────────────────────────────

function CompletionBar({ pct, doneSteps, totalSteps }) {
  const { t } = useTranslation();
  const color = pct === 100 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-400' : pct >= 30 ? 'bg-orange-400' : 'bg-red-500';
  return (
    <div data-testid="checklist-progress-bar">
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-[var(--aif-text-muted)]">
          {t('checklist.completion', { pct })}
        </span>
        <span className="text-[var(--aif-text-secondary)] font-medium">
          {doneSteps}/{totalSteps} {t('checklist.steps')}
        </span>
      </div>
      <div className="h-2 rounded-full bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ── StepCard ─────────────────────────────────────────────────────────────────

function StepCard({ step, deadlineId, token, onUpdated }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const isAuto = step.auto_check;
  const hasIssues = (step.missing?.length > 0) || (step.warnings?.length > 0);

  async function handleToggle(newStatus) {
    if (isAuto) return;
    setSaving(true);
    try {
      await apiClient.patch(`/api/v1/aif/subsidies/checklist/${deadlineId}/steps/${step.step}`, { status: newStatus }, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      onUpdated();
    } finally {
      setSaving(false);
    }
  }

  const statusBorder = step.status === 'done'
    ? 'border-green-700/50 bg-green-900/10'
    : step.status === 'partial'
    ? 'border-yellow-700/50 bg-yellow-900/10'
    : 'border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)]';

  return (
    <div
      className={`rounded-xl border ${statusBorder} overflow-hidden`}
      data-testid={`checklist-step-${step.step}`}
    >
      {/* Header row */}
      <button
        className="w-full flex items-start gap-3 px-4 py-3 text-left"
        onClick={() => setExpanded(e => !e)}
      >
        <StatusIcon status={step.status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-[var(--aif-text-primary)]">
              {step.step}. {step.title}
            </span>
            {isAuto && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-900/30 text-blue-400 border border-blue-700/50 shrink-0">
                {t('checklist.autoCheck')}
              </span>
            )}
          </div>
          {step.check_result && (
            <div className="text-xs text-[var(--aif-text-muted)] mt-0.5">
              {step.check_result}
            </div>
          )}
          {hasIssues && (
            <div className="text-xs text-orange-400 mt-0.5">
              ⚠️ {(step.missing || step.warnings || []).length} {t('checklist.missingItems')}
            </div>
          )}
        </div>
        <span className="text-[var(--aif-text-muted)] text-xs shrink-0 mt-1">
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-[var(--aif-border-default)] space-y-3">
          {/* Description */}
          <p className="text-xs text-[var(--aif-text-muted)] leading-relaxed">
            {step.description}
          </p>

          {/* Missing / warnings list */}
          {(step.missing?.length > 0 || step.warnings?.length > 0) && (
            <div className="space-y-1">
              {(step.missing || step.warnings || []).map((item, i) => (
                <div key={i} className="text-xs text-orange-300 flex items-start gap-1.5">
                  <span className="shrink-0 mt-0.5">•</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          )}

          {/* Options list (Öko-Regelungen etc.) */}
          {step.options?.length > 0 && (
            <div className="space-y-1">
              {step.options.map((opt, i) => (
                <div key={i} className="text-xs text-[var(--aif-text-secondary)] flex items-start gap-1.5">
                  <span className="shrink-0 text-[var(--aif-text-muted)]">•</span>
                  <span>{opt}</span>
                </div>
              ))}
            </div>
          )}

          {/* Help text */}
          {step.help_text && (
            <div className="text-xs text-[var(--aif-text-muted)] bg-[var(--aif-bg-input)] rounded-xl p-3 leading-relaxed border border-[var(--aif-border-default)]">
              ℹ️ {step.help_text}
            </div>
          )}

          {/* Action URL */}
          {step.action_url && (
            <a
              href={step.action_url}
              className="inline-flex items-center gap-1.5 text-xs text-[var(--aif-color-primary)] hover:underline"
            >
              → {t('checklist.openInApp')}
            </a>
          )}

          {/* Portal URL */}
          {step.portal_url && (
            <a
              href={step.portal_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[var(--aif-color-primary)] hover:underline"
            >
              🔗 {t('checklist.goToPortal')} →
            </a>
          )}

          {/* Documents list */}
          {step.documents?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase text-[var(--aif-text-muted)] font-semibold mb-1">
                {t('checklist.uploadedDocs')}
              </div>
              {step.documents.map((doc, i) => (
                <div key={i} className="text-xs text-[var(--aif-text-secondary)] flex items-center gap-1.5">
                  <span>📄</span>
                  <span>{doc.filename}</span>
                  {doc.size_bytes > 0 && (
                    <span className="text-[var(--aif-text-muted)]">
                      ({Math.round(doc.size_bytes / 1024)} KB)
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Manual step toggle buttons */}
          {!isAuto && (
            <div className="flex gap-2 pt-1" data-testid={`step-${step.step}-actions`}>
              <button
                onClick={() => handleToggle('done')}
                disabled={saving || step.status === 'done'}
                className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${
                  step.status === 'done'
                    ? 'bg-green-500/30 text-green-300 border border-green-600'
                    : 'bg-[var(--aif-bg-input)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)] hover:border-green-600 hover:text-green-400'
                }`}
              >
                ✓ {t('checklist.markDone')}
              </button>
              <button
                onClick={() => handleToggle('partial')}
                disabled={saving || step.status === 'partial'}
                className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${
                  step.status === 'partial'
                    ? 'bg-yellow-500/30 text-yellow-300 border border-yellow-600'
                    : 'bg-[var(--aif-bg-input)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)] hover:border-yellow-600 hover:text-yellow-400'
                }`}
              >
                ◑ {t('checklist.markPartial')}
              </button>
              {step.status !== 'open' && (
                <button
                  onClick={() => handleToggle('open')}
                  disabled={saving}
                  className="px-3 py-2 rounded-xl text-xs text-[var(--aif-text-muted)] bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] hover:border-red-700 hover:text-red-400 transition-all"
                >
                  ✕
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── SubsidyChecklist (main export) ──────────────────────────────────────────

export default function SubsidyChecklist({ deadlineId, token, onBack }) {
  const { t } = useTranslation();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    if (!token || !deadlineId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `/api/v1/aif/subsidies/checklist/${deadlineId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        setError('not_found');
        return;
      }
      const data = await res.json();
      setChecklist(data);
    } catch {
      setError('error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [deadlineId, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="checklist-loading">
        <div className="animate-spin w-6 h-6 rounded-full border-2 border-[var(--aif-color-primary)] border-t-transparent" />
      </div>
    );
  }

  if (error || !checklist) {
    return (
      <div className="text-sm text-[var(--aif-text-muted)] text-center py-8" data-testid="checklist-not-found">
        {t('checklist.notFound')}
      </div>
    );
  }

  const { program_name, deadline, days_remaining, steps, completion_pct,
          done_steps, total_steps, estimated_payment, payment_date_est } = checklist;

  return (
    <div className="space-y-4" data-testid="subsidy-checklist">
      {/* Back button */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)] transition-colors"
          data-testid="checklist-back"
        >
          ← {t('checklist.backToList')}
        </button>
      )}

      {/* Header */}
      <div className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
        <h2 className="text-base font-bold text-[var(--aif-text-primary)] mb-0.5">
          {program_name}
        </h2>
        <p className="text-xs text-[var(--aif-text-muted)] mb-3">
          {t('checklist.daysLeft', { days: days_remaining, deadline })}
        </p>
        <CompletionBar pct={completion_pct} doneSteps={done_steps} totalSteps={total_steps} />
      </div>

      {/* Estimated payment */}
      {estimated_payment > 0 && (
        <div className="rounded-xl border border-green-700/50 bg-green-900/10 px-4 py-3 flex items-center justify-between" data-testid="estimated-payment">
          <div>
            <div className="text-xs text-[var(--aif-text-muted)]">{t('checklist.estimatedPayment')}</div>
            <div className="text-lg font-bold text-green-400">
              {estimated_payment.toLocaleString('de-DE')} €
            </div>
          </div>
          {payment_date_est && (
            <div className="text-right">
              <div className="text-xs text-[var(--aif-text-muted)]">{t('checklist.paymentDate')}</div>
              <div className="text-sm font-semibold text-[var(--aif-text-secondary)]">
                {new Date(payment_date_est + 'T00:00:00').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Steps */}
      <div className="space-y-2" data-testid="checklist-steps">
        {steps.map(step => (
          <StepCard
            key={step.step}
            step={step}
            deadlineId={deadlineId}
            token={token}
            onUpdated={load}
          />
        ))}
      </div>

      {/* What's missing CTA */}
      {completion_pct < 100 && (
        <div className="rounded-xl border border-[var(--aif-color-primary)]/30 bg-[var(--aif-color-primary)]/10 px-4 py-3 text-center">
          <p className="text-xs text-[var(--aif-text-muted)] mb-2">
            {t('checklist.whatsLeftHint')}
          </p>
          <button
            className="text-sm font-semibold text-[var(--aif-color-primary)] hover:underline"
            data-testid="whats-left-btn"
          >
            {t('checklist.whatsLeft')}
          </button>
        </div>
      )}
    </div>
  );
}
